<template>
  <div class="trip-reports-grid-wrapper">
    <Grid
      class="trip-reports-grid"
      :data-items="reportsNoComments"
      :columns="columns"
      :scrollable="'none'"
      :loader="loading"
      data-cy="trip-reports-grid"
    >
      <template v-slot:totalFooter>
        <span>Total</span>
      </template>
      <template v-slot:sumFooter="{ props }">
        <span>{{ CurrencyFormatter.format(getSum(props.field)) }}</span>
      </template>
    </Grid>
    <div v-if="reportComment" class="trip-reports-grid__comments">
      <h3 class="trip-reports-grid__comments-header">General Comments</h3>
      <p>{{ reportComment.CompanyName }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import type { TripInfoReport } from "@/Domain/TripInfoReport";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import { computed, onBeforeMount, ref, watch } from "vue";
import { CurrencyFormatter } from "@/shared/helpers/formatHelper";
import { useExportStore } from "@/stores/useExportStore";
import {
  workbookOptions,
  type WorkbookOptions,
} from "@progress/kendo-vue-excel-export";
import { copyRowsToSheet } from "@/shared/helpers/exportHelper";

const exportStore = useExportStore();

const props = defineProps<{
  tripId: number;
}>();

const reports = ref<TripInfoReport[]>([]);
const loading = ref(false);

const reportsNoComments = computed(() =>
  reports.value.filter((report) => !report.IsRfrComment)
);

const reportComment = computed(() =>
  reports.value.find((report) => report.IsRfrComment)
);

const getSum = function (field: string) {
  return reports.value.reduce(
    (acc, report) =>
      acc + ((report[field as keyof TripInfoReport] as number) || 0),
    0
  );
};

const columns = ref([
  {
    title: "Visit ID",
    field: "VisitId",
  },
  {
    title: "Date \n Ticket#",
    field: "DateTicketNumber",
    footerCell: "totalFooter",
    footer: () => "Total",
  },
  {
    title: "Plant \n Company",
    field: "PlantCompany",
  },
  {
    title: "RC \n Location",
    field: "RcLocation",
  },
  {
    title: "Time To Plant",
    field: "TimeToPlant",
  },
  {
    title: "Time In Plant",
    field: "TimeInPlant",
  },
  {
    title: "Time To Home",
    field: "TimeToHome",
  },
  {
    title: "RFR Reimburse",
    field: "RfrReimbursement",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("RfrReimbursement"),
  },
  {
    title: "Ou Air",
    field: "OuAir",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("OuAir"),
  },
  {
    title: "OU Car Rental",
    field: "OuCarRental",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("OuCarRental"),
  },
  {
    title: "OU Misc",
    field: "OuMisc",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("OuMisc"),
  },
  {
    title: "OU Mileage",
    field: "OuMileage",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("OuMileage"),
  },
  {
    title: "RFR Pay",
    field: "RfrPay",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("RfrPay"),
  },
  {
    title: "Bill Co Fee",
    field: "BillCoFee",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("BillCoFee"),
  },
  {
    title: "Bill Co Exp",
    field: "BillCoExp",
    footerCell: "sumFooter",
    format: "{0:c}",
    footer: () => getSum("BillCoExp"),
  },
  {
    title: "Bill Code",
    field: "BillCode",
  },
] as GridColumnProps[]);

const getTripReportsAsync = async () => {
  loading.value = true;

  await handleResponsePromiseAsync(
    api.get("GetTripInfoReports", {
      tripId: props.tripId,
    }),
    (data) => {
      reports.value = data;
    },
    "TripReportsGrid.vue",
    "GetInfoTripReports"
  );

  loading.value = false;
};

const onExport = function (options: WorkbookOptions | null) {
  if (!options) return;
  const optionsTemp = workbookOptions({
    data: reportsNoComments.value,
    columns: columns.value,
  });

  copyRowsToSheet(options, optionsTemp);

  const commentOptionsTemp = workbookOptions({
    data: [reportComment.value],
    columns: [
      {
        title: "General Comments",
        field: "CompanyName",
      },
    ],
  });

  copyRowsToSheet(options, commentOptionsTemp);
};

exportStore.subscribe(onExport);

watch(
  () => props.tripId,
  () => {
    getTripReportsAsync();
  }
);

onBeforeMount(() => {
  getTripReportsAsync();
});
</script>

<style lang="scss">
.trip-reports-grid-wrapper {
  .trip-reports-grid__comments {
    margin-top: 20px;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #f9f9f9;

    &-header {
      margin-bottom: 12px;
    }
  }

  .trip-reports-grid {
    .k-column-title,
    .k-table-td {
      white-space: pre-wrap !important;
    }
  }
}
</style>
